import { useCallback, useEffect, useState } from "react";
import "./AddAdminStaff.css";

//import Permission from "../../administrator/roles/Permission";
import CommonMessage from "../../../common/CommonMessage";
import CommonDropdown from "../../../common/CommonDropdown";

import { GetHierarchyRole } from "../../../services/roleService";
import { addUser, GetUserByRoleId } from "../../../services/userService";

const AddAdminStaff = () => {
    const initialFormData = {
        roleName: "",
        pwd: "",
        email: "",
        mobileNo: "",
        status: "",
    };

    const initialErrors = {
        selectedRole: "",
        selectedUser: "",
        roleName: "",
        pwd: "",
        email: "",
        mobileNo: "",
        status: "",
        form: "",
    };

    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);

    const [selectedRole, setSelectedRole] = useState("");
    const [selectedUser, setSelectedUser] = useState("");

    const [formData, setFormData] = useState(initialFormData);

    const [errors, setErrors] = useState(initialErrors);

    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // =========================================
    // FORMAT ROLE HIERARCHY
    // =========================================
    const formatRoles = useCallback((rolesArray, parentPath = "") => {
        let result = [];

        rolesArray.forEach((role) => {
            const fullPath = parentPath
                ? `${parentPath} / ${role.roleName}`
                : role.roleName;

            result.push({
                id: role.id,
                name: fullPath,
            });

            if (role.children?.length > 0) {
                result = result.concat(
                    formatRoles(role.children, fullPath)
                );
            }
        });

        return result;
    }, []);

    // =========================================
    // FORMAT USERS
    // =========================================
    const formatUsers = useCallback((usersArray = []) => {
        return usersArray.map((user) => ({
            id: user.id,
            name: user.userName,
        }));
    }, []);

    // =========================================
    // FETCH ROLES
    // =========================================
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await GetHierarchyRole();

                if (response?.statusCode === 200 && response?.success) {
                    setRoles(formatRoles(response.data));
                } else {
                    setErrors((prev) => ({
                        ...prev,
                        form:
                            response?.message ||
                            "Failed to fetch roles",
                    }));
                }
            } catch (err) {
                setErrors((prev) => ({
                    ...prev,
                    form:
                        err.message || "Something went wrong",
                }));
            }
        };

        fetchRoles();
    }, [formatRoles]);

    // =========================================
    // FETCH USERS BY ROLE
    // =========================================
    useEffect(() => {
        if (!selectedRole) {
            setUsers([]);
            setSelectedUser("");
            return;
        }

        const fetchUsers = async () => {
            try {
                const response = await GetUserByRoleId(
                    selectedRole
                );

                if (response?.statusCode === 200 && response?.success) {
                    setUsers(formatUsers(response.data));
                } else {
                    setUsers([]);

                    setErrors((prev) => ({
                        ...prev,
                        form:
                            response?.message ||
                            "Failed to fetch users",
                    }));
                }
            } catch (err) {
                setUsers([]);

                setErrors((prev) => ({
                    ...prev,
                    form:
                        err.message || "Something went wrong",
                }));
            }
        };

        fetchUsers();
    }, [selectedRole, formatUsers]);

    // =========================================
    // HANDLE INPUT CHANGE
    // =========================================
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // remove field error while typing
        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    // =========================================
    // VALIDATION
    // =========================================
    const validate = () => {
        const newErrors = { ...initialErrors };

        let isValid = true;

        const {
            roleName,
            pwd,
            email,
            mobileNo,
            status,
        } = formData;

        // role
        if (!selectedRole) {
            newErrors.selectedRole =
                "Please select a role";
            isValid = false;
        }

        // user
        if (!selectedUser) {
            newErrors.selectedUser =
                "Please select a user";
            isValid = false;
        }

        // role name
        if (!roleName.trim()) {
            newErrors.roleName =
                "Please enter name";
            isValid = false;
        } else if (roleName.trim().length < 2) {
            newErrors.roleName =
                "Name must be at least 2 characters";
            isValid = false;
        }

        // password
        if (!pwd.trim()) {
            newErrors.pwd =
                "Please enter password";
            isValid = false;
        } else if (pwd.length < 6) {
            newErrors.pwd =
                "Password must be at least 6 characters";
            isValid = false;
        }

        // email
        if (!email.trim()) {
            newErrors.email =
                "Please enter email";
            isValid = false;
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ) {
            newErrors.email =
                "Please enter valid email";
            isValid = false;
        }

        // mobile
        if (!mobileNo.trim()) {
            newErrors.mobileNo =
                "Please enter mobile number";
            isValid = false;
        } else if (!/^\d{10}$/.test(mobileNo)) {
            newErrors.mobileNo =
                "Mobile number must be 10 digits";
            isValid = false;
        }

        // status
        if (status === "") {
            newErrors.status =
                "Please select status";
            isValid = false;
        }

        setErrors(newErrors);

        return isValid;
    };

    // =========================================
    // SUBMIT
    // =========================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        setMessage("");
        setErrors(initialErrors);

        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                roleId: Number(selectedRole),
                parentId: Number(selectedUser),
                username: formData.roleName.trim(),
                pwd: formData.pwd.trim(),
                email: formData.email.trim(),
                mobileNo: formData.mobileNo.trim(),
                status: formData.status === "true",
            };

            //console.log("Payload => ", payload);
            try {
                const response = await addUser(payload);

                if (response?.statusCode === 200) {
                    setMessage("User saved successfully");
                    //setFormData(initialFormData);
                    // reset form
                    setFormData(initialFormData);
                    setSelectedRole("");
                    setSelectedUser("");
                    setUsers([]);
                    setErrors({});
                } else {
                    setMessage("Failed to save User");
                }
            } catch (err) {
                console.error(err);
                setMessage("Something went wrong");
            }



        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                form:
                    err.message ||
                    "Failed to submit form",
            }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-3">
            

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* LEFT */}
                    <div className="col-lg-6">
                        <div className="custom-card">
                            <div className="card-header-red">
                                Admin Staff Details
                            </div>

                            <div className="card-body-custom">
                                {/* ROLE */}
                                <div className="mb-3">
                                    <CommonDropdown
                                        label="Select Role Type"
                                        options={roles}
                                        value={selectedRole}
                                        onChange={setSelectedRole}
                                        placeholder="Choose Role"
                                        error={
                                            <small className="text-danger">{errors.selectedRole}</small>
                                        }
                                    />
                                </div>

                                {/* USER */}
                                <div className="mb-3">
                                    <CommonDropdown
                                        label="Select User Type"
                                        options={users}
                                        value={selectedUser}
                                        onChange={setSelectedUser}
                                        placeholder="Choose User"
                                        error={
                                            <small className="text-danger">{errors.selectedUser}</small>
                                        }
                                    />
                                </div>

                                {/* NAME */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Enter Name
                                    </label>

                                    <input
                                        type="text"
                                        name="roleName"
                                        className="form-control"
                                        value={
                                            formData.roleName
                                        }
                                        maxLength={160}
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.roleName && (
                                        <div className="text-danger">
                                            {
                                                errors.roleName
                                            }
                                        </div>
                                    )}
                                </div>

                                {/* PASSWORD */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Password
                                    </label>

                                    <input
                                        type="password"
                                        name="pwd"
                                        className="form-control"
                                        value={formData.pwd}
                                        maxLength={160}
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.pwd && (
                                        <div className="text-danger">
                                            {errors.pwd}
                                        </div>
                                    )}
                                </div>

                                {/* EMAIL */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Email
                                    </label>

                                    <input
                                        type="email"
                                        name="email"
                                        className="form-control"
                                        value={
                                            formData.email
                                        }
                                        maxLength={100}
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.email && (
                                        <div className="text-danger">
                                            {
                                                errors.email
                                            }
                                        </div>
                                    )}
                                </div>

                                {/* MOBILE */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Mobile No
                                    </label>

                                    <input
                                        type="text"
                                        name="mobileNo"
                                        className="form-control"
                                        value={
                                            formData.mobileNo
                                        }
                                        maxLength={10}
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.mobileNo && (
                                        <div className="text-danger">
                                            {
                                                errors.mobileNo
                                            }
                                        </div>
                                    )}
                                </div>

                                {/* STATUS */}
                                <div className="mb-3">
                                    <select
                                        name="status"
                                        className={`form-select ${errors.status
                                                ? "is-invalid"
                                                : ""
                                            }`}
                                        value={
                                            formData.status
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    >
                                        <option value="">
                                            Select Status
                                        </option>

                                        <option value="true">
                                            Active
                                        </option>

                                        <option value="false">
                                            Inactive
                                        </option>
                                    </select>

                                    {errors.status && (
                                        <small className="text-danger">
                                            {
                                                errors.status
                                            }
                                        </small>
                                    )}
                                </div>

                                {/* FORM ERROR */}
                                {errors.form && (
                                    <div className="text-danger mb-3">
                                        {errors.form}
                                    </div>
                                )}

                                {/* SUBMIT */}
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={loading}
                                >
                                    {loading
                                        ? "Submitting..."
                                        : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            {message && <CommonMessage text={message} />}
        </div>
    );
};

export default AddAdminStaff;